import React, { useCallback, useEffect, useState } from "react";
import { CoreContainer, Notification } from "./style";
import { Card, CardGroup, SearchInput } from "../product/style";
import { Spinner, UncontrolledTooltip } from "reactstrap";
import { useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import useSearchParamHandler from "../utils";
import Header from "./Header";
import { ColorPicker } from "../project/style";
import { getLaminates } from "../project/service";
import NoImage from "../assets/img/no_image.png";

const LaminateCatalog = () => {
  const [laminates, setLaminates] = useState([]);
  const [searchParams] = useSearchParams();
  const replaceSearchParam = useSearchParamHandler();
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ type: "", message: "" });
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  const onDismissNotification = () => {
    setIsNotificationVisible(false);
  };

  const handleSearch = (searchValue) => {
    setSearchText(searchValue);
    debounceSearch(searchValue, searchParams);
  };

  const debouncedSearch = (searchTerm, searchParams) => {
    replaceSearchParam("search", searchTerm, searchParams);
  };

  const debounceSearch = useCallback(debounce(debouncedSearch, 500), []);
  const searchTerm = searchParams.get("search") || "";

  useEffect(() => {
    return () => {
      handleSearch("");
      setLaminates([]);
    };
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setIsLoading(true);

      getLaminates({ searchTerm })
        .then((laminates) => setLaminates(laminates))
        .catch(({ error }) => {
          setIsNotificationVisible(true);
          setNotification({ type: "error", message: error });
        })
        .finally(() => setIsLoading(false));
    }
  }, [searchTerm]);

  return (
    <>
      {notification.type && (
        <Notification
          color={notification.type === "error" ? "danger" : "success"}
          isOpen={isNotificationVisible}
          toggle={onDismissNotification}
        >
          {notification.message}
        </Notification>
      )}
      <Header />
      <div className="px-3 px-md-4 py-1">
        <div className="pt-3 position-relative">
          <SearchInput
            className="unmask"
            autoFocus
            type="search"
            placeholder={"Search Laminates"}
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
        {isLoading ? (
          <CoreContainer>
            <Spinner>Loading...</Spinner>
          </CoreContainer>
        ) : laminates.length === 0 ? (
          <Card className="d-flex justify-content-center align-items-center empty px-3 mt-3">
            {searchParams.get("search") && (
              <span>No laminates found! Try a different keyword.</span>
            )}
            {!searchParams.get("search") && (
              <span>Type to search laminates</span>
            )}
          </Card>
        ) : (
          <CardGroup className="modules-listing row row-gap-3 my-3">
            {laminates.map((laminate, index) => (
              <>
                <UncontrolledTooltip
                  target={`laminate_${laminate.id}`}
                  placement="bottom"
                >
                  Click to copy the code
                </UncontrolledTooltip>
                <div
                  id={`laminate_${laminate.id}`}
                  key={index}
                  className="col-6 col-sm-4 col-md-3 col-xl-2"
                  onClick={() => {
                    navigator.clipboard.writeText(
                      laminate.sku || laminate.identifier
                    );
                    setCopiedId(laminate.id);

                    setTimeout(() => {
                      setCopiedId(null);
                    }, 5000);
                  }}
                >
                  <Card className="module-card d-flex flex-column align-items-center">
                    <ColorPicker>
                      <img
                        className="h-100 w-100"
                        src={laminate.image ? laminate.image : NoImage}
                      />
                    </ColorPicker>
                    <p className="m-0 text-center text-muted">
                      {laminate.sku || laminate.identifier}
                    </p>
                    <p className="m-0 text-center">{laminate.name}</p>
                    {copiedId === laminate.id && (
                      <div className="copied-text">Copied</div>
                    )}
                  </Card>
                </div>
              </>
            ))}
          </CardGroup>
        )}
      </div>
    </>
  );
};

export default LaminateCatalog;
